import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Observable } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorIntl, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { MenuComponent } from '../../../shared/components/menu/menu.component';
import { FooterComponent } from '../../../shared/components/footer/footer.component';
import { LoginValidationComponent } from '../../../book-create/login-validation.component/login-validation.component';
import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { AdminPrintCompanyListRequest } from '../../../shared/services/requests/platform/admin-print-company-list.request';
import {
  AdminPrintCompanyActionResponse,
  AdminPrintCompanyIndicatorsResponse,
  PrintCompanyKitItemStruct,
  PrintCompanyKitStruct,
  PrintCompanyListResponse,
} from '../../../shared/services/responses/platform/admin-print-company-list.response';
import { AdminPrintCompanyService } from '../../../shared/services/apis/platform/admin-print-company.service';
import { AdminPrintCompanyItemsDialogComponent } from './admin-print-company-items-dialog.component';
import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal.component';
import { BookPrintService } from '../../../shared/services/apis/platform/book-print.service';
import { UtcDatePipe, parseUtc } from '../sales-list/utc-date.pipe';
import { SalesPaginatorIntl } from '../sales-list/sales-list.component';
import { SALES_STATUS, durationLabel, statusClass } from '../sales-list/sales-status';

type DateField = 'sale' | 'label' | 'sent';

@Component({
  selector: 'app-admin-print-company-list',
  standalone: true,
  templateUrl: './admin-print-company-list.component.html',
  styleUrl: './admin-print-company-list.component.css',
  imports: [
    CommonModule, ReactiveFormsModule, MenuComponent, FooterComponent, LoginValidationComponent,
    MatIconModule, MatTooltipModule, MatProgressSpinnerModule, MatButtonModule, MatSelectModule, MatFormFieldModule,
    MatInputModule, MatDatepickerModule, MatNativeDateModule, MatSlideToggleModule, MatMenuModule, MatPaginatorModule,
    UtcDatePipe,
  ],
  providers: [{ provide: MatPaginatorIntl, useClass: SalesPaginatorIntl }],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminPrintCompanyListComponent implements OnInit {
  isLoading = false;
  /** Ação (iniciar produção / confirmar envio / ZIP) em andamento. */
  isActing = false;
  form!: FormGroup;

  readonly STATUS = SALES_STATUS;
  readonly statusOptions = ['Todos', SALES_STATUS.aguardaProducao, SALES_STATUS.emProducao, SALES_STATUS.enviado, SALES_STATUS.entregue];
  readonly dateFieldOptions: { value: DateField; label: string }[] = [
    { value: 'sale', label: 'Data da venda' },
    { value: 'label', label: 'Etiqueta emitida' },
    { value: 'sent', label: 'Saída da gráfica' },
  ];
  readonly pageSizeOptions = [25, 50, 100];

  listBookPrint: any[] = [];
  list: PrintCompanyKitStruct[] = [];
  total = 0;
  page = 1;
  pageSize = 25;

  indicators: AdminPrintCompanyIndicatorsResponse | null = null;

  statusClass = statusClass;
  durationLabel = durationLabel;

  constructor(
    private fb: FormBuilder,
    private adminPrintCompanyService: AdminPrintCompanyService,
    private alert: AlertService,
    private bookPrintService: BookPrintService,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
  ) { }

  ngOnInit(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    this.form = this.fb.group({
      search: [''],
      status: ['Todos'],
      listIdBookPrint: [[] as number[]],
      dateField: ['sale' as DateField],
      dateStart: [null],
      dateEnd: [null],
      onlyLate: [false],
    });
  }

  onLoginValidated(isLogged: boolean) {
    if (!isLogged) return;
    this.loadBookPrints();
    this.fetchIndicators();
    this.fetch();
  }

  // ---------- Indicadores ----------
  fetchIndicators(): void {
    this.adminPrintCompanyService.indicators().subscribe({
      next: (res) => {
        if (!res.isError) this.indicators = res;
        this.cdr.markForCheck();
      },
      error: () => this.cdr.markForCheck(),
    });
  }

  /** Clique no card de etapa aplica/remove o filtro de status. */
  toggleStatus(status: string): void {
    const current = this.form.value.status;
    this.form.patchValue({ status: current === status ? 'Todos' : status });
    this.applyFilters();
  }

  avgProductionLabel(): string {
    const h = this.indicators?.avgProductionHours30d;
    return h === null || h === undefined ? '—' : durationLabel(h);
  }

  // ---------- Lista ----------
  fetch(): void {
    this.isLoading = true;
    this.cdr.markForCheck();
    this.adminPrintCompanyService.postList(this.buildRequest()).subscribe({
      next: (res: PrintCompanyListResponse) => {
        this.isLoading = false;
        if (res.isError) {
          this.alert.show('Erro', res.errorDescription || 'Falha ao carregar a produção.', AlertType.error);
          this.cdr.markForCheck();
          return;
        }
        this.list = res.list ?? [];
        this.total = res.total ?? 0;
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.isLoading = false;
        this.alert.show('Erro', err?.message || 'Erro inesperado ao carregar a lista.', AlertType.error);
        this.cdr.markForCheck();
      },
    });
  }

  loadBookPrints(): void {
    this.bookPrintService.getActiveByNumPages(1).subscribe({
      next: (res: any) => {
        if (res?.isError) return;
        this.listBookPrint = res.listBookPrintStruct ?? res.listBookPrint ?? [];
        this.cdr.markForCheck();
      }
    });
  }

  applyFilters(): void {
    this.page = 1;
    this.fetch();
  }

  resetFilters(): void {
    this.form.reset({ search: '', status: 'Todos', listIdBookPrint: [], dateField: 'sale', dateStart: null, dateEnd: null, onlyLate: false });
    this.applyFilters();
  }

  onPage(ev: PageEvent): void {
    this.page = ev.pageIndex + 1;
    this.pageSize = ev.pageSize;
    this.fetch();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  private buildRequest(): AdminPrintCompanyListRequest {
    const v = this.form.value;
    const printIds: number[] = (v.listIdBookPrint || []).map((x: any) => Number(x)).filter((x: number) => Number.isFinite(x) && x > 0);
    return {
      page: this.page,
      pageSize: this.pageSize,
      search: (v.search || '').trim() || null,
      listStatus: v.status && v.status !== 'Todos' ? [v.status] : null,
      listIdBookPrint: printIds.length ? printIds : null,
      dateField: v.dateField || 'sale',
      dateTimeStart: this.startOfDayIso(v.dateStart),
      dateTimeEnd: this.endOfDayIso(v.dateEnd),
      onlyLate: !!v.onlyLate,
    };
  }

  private startOfDayIso(date: Date | null): string | null {
    if (!date) return null;
    const d = new Date(date); d.setHours(0, 0, 0, 0);
    return d.toISOString();
  }

  private endOfDayIso(date: Date | null): string | null {
    if (!date) return null;
    const d = new Date(date); d.setHours(23, 59, 59, 999);
    return d.toISOString();
  }

  private refreshAll(): void {
    this.fetch();
    this.fetchIndicators();
  }

  // ---------- Ações de produção ----------
  isWaiting(s: PrintCompanyKitStruct): boolean { return s.statusComputed === SALES_STATUS.aguardaProducao; }
  isInProduction(s: PrintCompanyKitStruct): boolean { return s.statusComputed === SALES_STATUS.emProducao; }

  startProduction(s: PrintCompanyKitStruct): void {
    if (!s?.idKitPayment || this.isActing) return;
    this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Iniciar produção',
        description: `Confirma o início da produção do pedido #${s.saleNumber} (${s.booksCount} livro${s.booksCount === 1 ? '' : 's'}, ${s.pagesCount} páginas)? A data e hora de início serão registradas agora.`,
        buttonConfirm: 'Sim, iniciar produção',
        buttonClose: 'Cancelar',
      },
    }).afterClosed().subscribe(result => {
      if (result?.confirm) this.runAction(this.adminPrintCompanyService.startProduction(s.idKitPayment), 'Produção iniciada.');
    });
  }

  confirmSend(s: PrintCompanyKitStruct): void {
    if (!s?.idKitPayment || this.isActing) return;
    if (!this.isInProduction(s)) {
      this.alert.show('Atenção', 'Inicie a produção antes de confirmar o envio.', AlertType.warning);
      return;
    }
    this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Confirmar envio',
        description: `Ao confirmar, você está confirmando a produção e envio do pedido #${s.saleNumber}, contendo ${s.booksCount} livro${s.booksCount === 1 ? '' : 's'}. O cliente será avisado por e-mail. Deseja continuar?`,
        buttonConfirm: 'Sim, confirmar envio',
        buttonClose: 'Cancelar',
      },
    }).afterClosed().subscribe(result => {
      if (result?.confirm) this.runAction(this.adminPrintCompanyService.confirmSend(s.idKitPayment), 'Envio confirmado.');
    });
  }

  private runAction(obs: Observable<AdminPrintCompanyActionResponse>, okMessage: string): void {
    this.isActing = true;
    this.cdr.markForCheck();
    obs.subscribe({
      next: (resp) => {
        this.isActing = false;
        if (resp?.isError) {
          this.alert.show('Erro', resp.errorDescription || 'Falha ao executar a ação.', AlertType.error);
        } else {
          this.alert.show('Sucesso', resp.alreadyDone ? 'Esta ação já tinha sido registrada.' : okMessage, AlertType.success);
          this.refreshAll();
        }
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.isActing = false;
        this.alert.show('Erro', err?.message || 'Erro inesperado ao executar a ação.', AlertType.error);
        this.cdr.markForCheck();
      },
    });
  }

  openItemsModal(s: PrintCompanyKitStruct) {
    this.dialog.open(AdminPrintCompanyItemsDialogComponent, {
      width: '720px',
      maxWidth: '95vw',
      data: s
    });
  }

  openUrl(url?: string | null) {
    if (!url) return;
    window.open(url, '_blank', 'noopener');
  }

  // ---------- Helpers de template ----------
  /** Formatos (tamanho | papel) distintos dos itens. */
  formatsOf(s: PrintCompanyKitStruct): string[] {
    const set = new Set<string>();
    for (const it of s.listPrintCompanyKitItemStruct || []) {
      if (it.bookPrintSize || it.bookPrintDescription) set.add(`${it.bookPrintSize || ''}${it.bookPrintSize && it.bookPrintDescription ? ' | ' : ''}${it.bookPrintDescription || ''}`);
    }
    return [...set];
  }

  /** Marcos da linha: Etiqueta → Início da produção → Saída → Entregue. */
  steps(s: PrintCompanyKitStruct): { label: string; date?: string | null }[] {
    return [
      { label: 'Etiqueta emitida', date: s.datetimeLabelGenerated ?? s.datetimeXmlInclusion },
      { label: 'Início da produção', date: s.datetimeProductionStarted },
      { label: 'Saída da gráfica', date: s.sendDateTime },
      { label: 'Entregue', date: s.datetimeDelivered },
    ];
  }

  isCurrentStep(s: PrintCompanyKitStruct, index: number): boolean {
    const st = this.steps(s);
    for (let i = 0; i < st.length; i++) if (!st[i].date) return i === index;
    return false;
  }

  /** Tempo na etapa atual ou tempo total de produção (histórico). */
  stageTime(s: PrintCompanyKitStruct): string {
    const now = Date.now();
    if (this.isWaiting(s)) {
      const since = parseUtc(s.datetimeLabelGenerated ?? s.datetimeXmlInclusion);
      return since ? `aguardando há ${durationLabel((now - since.getTime()) / 36e5)}` : '';
    }
    if (this.isInProduction(s)) {
      const since = parseUtc(s.datetimeProductionStarted);
      return since ? `em produção há ${durationLabel((now - since.getTime()) / 36e5)}` : '';
    }
    const start = parseUtc(s.datetimeProductionStarted);
    const end = parseUtc(s.sendDateTime);
    if (start && end && start < end) return `produzido em ${durationLabel((end.getTime() - start.getTime()) / 36e5)}`;
    return '';
  }

  trackBySale = (_: number, s: PrintCompanyKitStruct) => s?.idKitPayment ?? _;

  // ---------- ZIP / PDF (regras preservadas da versão anterior) ----------
  private async fetchFileAsArrayBuffer(url: string, label?: string): Promise<ArrayBuffer | null> {
    try {
      const resp = await fetch(url);
      if (!resp.ok) {
        console.warn('[ZIP] Erro HTTP ao baixar', label || '', url, resp.status, resp.statusText);
        return null;
      }
      return await resp.arrayBuffer();
    } catch (err) {
      console.error('[ZIP] Exceção ao baixar', label || '', url, err);
      return null;
    }
  }

  private buildResumoPdfArrayBuffer(s: PrintCompanyKitStruct): ArrayBuffer {
    const doc = new jsPDF();

    const saleNumber = s.saleNumber ?? '';
    const title = `Resumo do pedido #${saleNumber}`;

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(title, 14, 18);

    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');

    const saleDate = parseUtc(s.saleDateTime);
    const startDate = parseUtc(s.datetimeProductionStarted);
    const sendDate = parseUtc(s.sendDateTime);

    const fmt = (d: Date | null) =>
      d ? d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

    let y = 28;

    doc.text(`Data da venda: ${fmt(saleDate)}`, 14, y); y += 6;
    if (startDate) { doc.text(`Início da produção: ${fmt(startDate)}`, 14, y); y += 6; }
    if (sendDate) { doc.text(`Enviado em: ${fmt(sendDate)}`, 14, y); y += 6; }

    if (s.clientName) {
      doc.text(`Cliente: ${s.clientName}`, 14, y);
      y += 6;
    }

    const hasAddress = s.street || s.addressNumber || s.city || s.state || s.zipcode;
    if (hasAddress) {
      const addressParts: string[] = [];
      if (s.zipcode) addressParts.push(s.zipcode);
      if (s.street) {
        let street = s.street;
        if (s.addressNumber) street += `, ${s.addressNumber}`;
        addressParts.push(street);
      }
      if (s.city) {
        let city = s.city;
        if (s.state) city += ` / ${s.state}`;
        addressParts.push(city);
      }
      doc.text(`Endereço: ${addressParts.join(' | ')}`, 14, y);
      y += 8;
    } else {
      y += 4;
    }

    const items = s.listPrintCompanyKitItemStruct || [];

    const body = items.map((it: PrintCompanyKitItemStruct) => [
      it.quantity != null ? it.quantity.toString() : '',
      it.bookTitle || '',
      `${it.bookPrintSize || ''} | ${it.bookPrintDescription || ''}`
    ]);

    autoTable(doc, {
      startY: y,
      head: [['Qtd', 'Livro', 'Formato']],
      body,
      styles: { fontSize: 10 },
      headStyles: { fillColor: [240, 240, 240] },
      theme: 'grid'
    });

    const totalBooks = items.reduce((acc, it) => acc + (it.quantity || 0), 0);
    const finalY = (doc as any).lastAutoTable?.finalY || y;

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(`Total de livros: ${totalBooks}`, 14, finalY + 10);

    return doc.output('arraybuffer');
  }

  async downloadAllFiles(s: PrintCompanyKitStruct): Promise<void> {
    if (!s || this.isActing) return;

    this.isActing = true;
    this.cdr.markForCheck();

    try {
      const zip = new JSZip();
      const saleNumber = s.saleNumber ?? 'pedido';

      // 1) PDF de resumo do pedido
      zip.file(`resumo-pedido-${saleNumber}.pdf`, this.buildResumoPdfArrayBuffer(s));

      const tasks: Promise<void>[] = [];

      const resolveFileNameFromUrl = (url: string, fallback: string): string => {
        try {
          const clean = url.split('?')[0];
          const parts = clean.split('/');
          return parts[parts.length - 1] || fallback;
        } catch {
          return fallback;
        }
      };

      const add = (url: string | null | undefined, fallback: string, label: string) => {
        if (!url) return;
        tasks.push((async () => {
          const buf = await this.fetchFileAsArrayBuffer(url, label);
          if (buf) zip.file(resolveFileNameFromUrl(url, fallback), buf);
        })());
      };

      // 2) DANFE, 3) XML da NFe, 4) Carta remessa (Correios)
      add(s.linkDanfe, `danfe-${saleNumber}.pdf`, 'DANFE');
      add(s.linkXml, `nfe-${saleNumber}.xml`, 'XML');
      add(s.linkCorreiosCartaRemessa, `carta-remessa-${saleNumber}.pdf`, 'Carta remessa');

      // 5) Itens: capas, contracapas, miolos
      (s.listPrintCompanyKitItemStruct || []).forEach((it: PrintCompanyKitItemStruct, idx: number) => {
        const baseNameSafe = (it.bookTitle || `livro-${idx + 1}`).replace(/[\\/:*?"<>|]/g, '_');
        add(it.bookCoverUrl, `capa-${baseNameSafe}.pdf`, `Capa: ${baseNameSafe}`);
        const backCoverUrl = (it as any).bookBackCoverUrl as string | undefined ?? (it as any).bookContraCapaUrl as string | undefined;
        add(backCoverUrl, `contracapa-${baseNameSafe}.pdf`, `Contracapa: ${baseNameSafe}`);
        add(it.bookPrintUrl, `miolo-${baseNameSafe}.pdf`, `Miolo: ${baseNameSafe}`);
      });

      await Promise.all(tasks);

      const blob = await zip.generateAsync({ type: 'blob' });
      saveAs(blob, `${saleNumber}.zip`);
    } catch (e) {
      this.alert.show('Erro', 'Falha ao gerar o pacote de arquivos para download.', AlertType.error);
    } finally {
      this.isActing = false;
      this.cdr.markForCheck();
    }
  }
}
